/**
 * CloudSyncTab.tsx
 * 클라우드 동기화 설정 탭 (UsageManagementPage 내 탭)
 * - 동기화 설정 (API 키, URL, ON/OFF, 연결 테스트)
 * - 동기화 상태 (마지막 동기화, 성공/실패 건수)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  KeyIcon,
  SignalIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '../../../../services/authApi';

// ================================================================
// Types
// ================================================================

interface SyncStatus {
  enabled: boolean;
  connected: boolean;
  last_sync: string | null;
  total_synced_logs: number;
  total_synced_members: number;
  subscription: {
    active: boolean;
    subscription_status: string;
    subscription_plan: string;
    monthly_count: number;
    free_quota: number;
  } | null;
}

interface SyncConfig {
  cloud_sync_enabled: boolean;
  cloud_sync_api_url: string;
  cloud_sync_api_key_masked: string;
  has_api_key: boolean;
}

// ================================================================
// Component
// ================================================================

const CloudSyncTab: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 동기화 설정 state
  const [syncConfig, setSyncConfig] = useState<SyncConfig | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiUrl, setApiUrl] = useState('https://api.newattend.com');
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        apiClient.get('/v1/sync/status'),
        apiClient.get('/v1/sync/config'),
      ]);

      // [0] sync status
      if (results[0].status === 'fulfilled' && results[0].value.data) {
        setSyncStatus(results[0].value.data);
      }

      // [1] sync config
      if (results[1].status === 'fulfilled' && results[1].value.data) {
        const data = results[1].value.data;
        setSyncConfig(data);
        setApiUrl(data.cloud_sync_api_url || 'https://api.newattend.com');
        setSyncEnabled(data.cloud_sync_enabled || false);
      }
    } catch (err: any) {
      setError(err?.message || '데이터를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFullSync = async () => {
    setSyncing(true);
    try {
      const res = await apiClient.post('/v1/sync/full-sync');
      if (res.data?.success) {
        alert(`동기화 완료!\n회원: ${res.data.synced?.members || 0}건\n학부모 연결: ${res.data.synced?.parent_links || 0}건\n입출입 기록: ${res.data.synced?.access_logs || 0}건`);
        fetchData();
      } else {
        alert('동기화 중 오류가 발생했습니다: ' + (res.data?.errors?.join(', ') || ''));
      }
    } catch (err: any) {
      alert('동기화 실패: ' + (err?.response?.data?.detail || err?.message || ''));
    } finally {
      setSyncing(false);
    }
  };

  // 설정 저장
  const handleSaveConfig = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const payload: any = {
        cloud_sync_enabled: syncEnabled,
        cloud_sync_api_url: apiUrl,
      };
      if (apiKey) {
        payload.cloud_sync_api_key = apiKey;
      }

      const res = await apiClient.post('/v1/sync/config', payload);
      if (res.data?.success) {
        setSaveMessage('설정이 저장되었습니다');
        setApiKey('');
        fetchData();
      } else {
        setSaveMessage('저장 실패');
      }
    } catch (err: any) {
      setSaveMessage('저장 실패: ' + (err?.response?.data?.detail || err?.message || ''));
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // 연결 테스트
  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiClient.post('/v1/sync/test-connection');
      setTestResult({
        success: res.data?.success || false,
        message: res.data?.message || '알 수 없는 결과',
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err?.response?.data?.detail || err?.message || '연결 테스트 실패',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
        동기화 설정을 불러오는 중...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ============================================================ */}
      {/* 클라우드 동기화 설정 */}
      {/* ============================================================ */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyIcon className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">클라우드 동기화 설정</h2>
        </div>

        {/* 동기화 상태 표시 */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-gray-600">동기화 상태:</span>
          {syncEnabled && syncConfig?.has_api_key ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              활성
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              비활성
            </span>
          )}
        </div>

        <div className="space-y-4">
          {/* API 키 입력 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API 키
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                syncConfig?.has_api_key
                  ? `현재: ${syncConfig.cloud_sync_api_key_masked} (변경 시 새 키 입력)`
                  : '관리자에게 받은 API 키를 붙여넣기 해주세요'
              }
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <p className="mt-1 text-xs text-gray-400">
              슈퍼관리자 페이지에서 발급받은 동기화 API 키를 입력합니다
            </p>
          </div>

          {/* 서버 URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              서버 URL
            </label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              placeholder="https://api.newattend.com"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <p className="mt-1 text-xs text-gray-400">
              기본값: https://api.newattend.com (변경할 필요 없음)
            </p>
          </div>

          {/* 동기화 ON/OFF 토글 */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-700">동기화 활성화</p>
              <p className="text-xs text-gray-400">ON으로 설정하면 입출입 시 자동으로 클라우드에 전송됩니다</p>
            </div>
            <button
              onClick={() => setSyncEnabled(!syncEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                syncEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  syncEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* 버튼들 */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleTestConnection}
              disabled={testing || !syncConfig?.has_api_key}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <SignalIcon className={`h-4 w-4 ${testing ? 'animate-pulse' : ''}`} />
              {testing ? '테스트 중...' : '연결 테스트'}
            </button>
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>

          {/* 테스트 결과 */}
          {testResult && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${
              testResult.success
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircleIcon className="h-5 w-5 text-red-500" />
                )}
                {testResult.message}
              </div>
            </div>
          )}

          {/* 저장 결과 */}
          {saveMessage && (
            <div className="mt-2 text-sm text-blue-600">
              {saveMessage}
            </div>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* 동기화 상태 */}
      {/* ============================================================ */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">동기화 상태</h2>
          </div>
          <button
            onClick={handleFullSync}
            disabled={syncing}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? '동기화 중...' : '지금 동기화'}
          </button>
        </div>
        {syncStatus ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {syncStatus.connected ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              ) : syncStatus.enabled ? (
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-gray-400" />
              )}
              <span className="text-sm">
                {syncStatus.connected
                  ? '클라우드 연결됨'
                  : syncStatus.enabled
                    ? '연결 시도 중...'
                    : '동기화 비활성화'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">마지막 동기화</p>
                <p className="text-sm font-medium text-gray-900">
                  {syncStatus.last_sync
                    ? new Date(syncStatus.last_sync).toLocaleString('ko-KR')
                    : '없음'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">동기화된 기록</p>
                <p className="text-sm font-medium text-gray-900">
                  {(syncStatus.total_synced_logs || 0).toLocaleString()}건
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">동기화된 회원</p>
                <p className="text-sm font-medium text-gray-900">
                  {(syncStatus.total_synced_members || 0).toLocaleString()}명
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <XCircleIcon className="h-5 w-5" />
            <span>동기화가 설정되지 않았습니다</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CloudSyncTab;
